import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { PainelVisivelContext } from "../contexto/painelVisivel";

export interface EstadoRequisicao<T> {
  dados: T | null;
  carregando: boolean;
  erro: string | null;
  recarregar: () => void;
}

export const mensagemErro = (erro: any): string => {
  const corpo = erro && erro.response && erro.response.data;
  if (corpo && corpo.mensagem) {
    return corpo.mensagem;
  }
  if (erro && erro.code === "ECONNABORTED") {
    return "A consulta demorou mais que o esperado. Tente um período menor ou refine os filtros.";
  }
  return "Não foi possível carregar os dados. Tente novamente.";
};

export function useRequisicao<T>(
  requisicao: () => Promise<T>,
  dependencias: any[],
  habilitada = true,
  dependenciasDePaginacao: any[] = []
): EstadoRequisicao<T> {
  const [carregado, setCarregado] = useState<{ chaveFiltro: string; dados: T } | null>(null);
  const [carregando, setCarregando] = useState(habilitada);
  const [erro, setErro] = useState<string | null>(null);
  const [gatilho, setGatilho] = useState(0);
  // Painel de aba oculto continua montado; adiar a requisicao evita que uma troca
  // de filtro dispare o refetch de todas as abas ja visitadas de uma vez.
  const visivel = useContext(PainelVisivelContext);
  const ativa = habilitada && visivel;

  const chaveFiltro = JSON.stringify(dependencias);
  const chave = JSON.stringify([dependencias, dependenciasDePaginacao]);

  const recarregar = useCallback(() => setGatilho((valor) => valor + 1), []);

  const chaveAnterior = useRef(chave);

  useEffect(() => {
    const trocouChave = chaveAnterior.current !== chave;
    if (trocouChave) {
      chaveAnterior.current = chave;
      // Painel de aba oculto continua montado: sem limpar aqui, o erro de um filtro
      // que nao existe mais reaparece ao voltar para a aba.
      setErro(null);
    }
    if (!ativa) {
      // Com a chave ja trocada o dado em tela e de outro filtro; manter carregando
      // evita um quadro sem indicacao de recarga ao voltar para a aba.
      setCarregando(habilitada && trocouChave);
      return undefined;
    }
    let ativo = true;
    setCarregando(true);
    setErro(null);
    requisicao()
      .then((resultado) => {
        if (ativo) {
          setCarregado({ chaveFiltro, dados: resultado });
        }
      })
      .catch((falha) => {
        if (ativo) {
          setErro(mensagemErro(falha));
        }
      })
      .then(() => {
        if (ativo) {
          setCarregando(false);
        }
      });
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, gatilho, ativa]);

  const dados = carregado && carregado.chaveFiltro === chaveFiltro ? carregado.dados : null;

  return { dados, carregando, erro, recarregar };
}
